// Turns the published /faq copy into plain chat answers and picks the best
// match for a question. Pure (no i18n, no React) so Mocha can import it.
// The on-device model never sees the full FAQ — only the one answer we return.

export type FaqAnswerPart = string | string[];

export interface FaqQuestion {
  id?: string;
  q?: string;
  a?: FaqAnswerPart[];
}

export interface FaqSection {
  id?: string;
  questions?: FaqQuestion[];
}

export interface FaqIntro {
  heading?: string;
  body?: string[];
}

export interface FaqStillStuck {
  heading?: string;
  body?: string[];
}

export interface FaqEntry {
  id: string;
  q: string;
  text: string;
}

const STOP = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'of',
  'to',
  'for',
  'with',
  'in',
  'on',
  'at',
  'by',
  'from',
  'i',
  'my',
  'me',
  'you',
  'your',
  'it',
  'its',
  'this',
  'that',
  'we',
  'our',
  'be',
  'am',
  'was',
  'were',
  'been',
  'than',
  'then',
  'just',
  'into',
  'onto',
  'about',
  'if',
  'so',
  'not',
  'no',
  'yes',
]);

// Kept out of the content overlap so "how do I add airpods" cannot match
// "How do I share my cart?" on the question words alone.
const QUESTION_WORDS = new Set([
  'how',
  'what',
  'why',
  'when',
  'where',
  'which',
  'who',
  'can',
  'does',
  'did',
  'is',
  'are',
  'do',
]);

const words = (s: string): string[] => {
  const found = s.toLowerCase().match(/[a-z0-9]+/g);
  if (!found) return [];
  return found.filter((w) => w.length > 2 && !STOP.has(w));
};

const contentWords = (tokens: string[]): string[] => tokens.filter((w) => !QUESTION_WORDS.has(w));

function absolutize(href: string, rootUrl: string): string {
  const h = href.trim();
  if (/^https?:\/\//i.test(h)) return h;
  const root = rootUrl.replace(/\/$/, '');
  if (h.startsWith('/')) return `${root}${h}`;
  return `${root}/${h}`;
}

function looksLikeUrl(text: string): boolean {
  return /^https?:\/\//i.test(text) || /^[\w.-]+\.[a-z]{2,}(\/\S*)?$/i.test(text);
}

function decodeEntities(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function stripTags(s: string): string {
  return decodeEntities(s.replace(/<[^>]+>/g, ''));
}

// Chat only linkifies raw https URLs, so anchors become absolute URLs.
function inlineToText(html: string, rootUrl: string): string {
  const withLinks = html.replace(
    /<a\b[^>]*href=["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi,
    (_m, href: string, label: string) => {
      const url = absolutize(href, rootUrl);
      const text = stripTags(label).trim();
      if (!url) return text;
      if (!text || looksLikeUrl(text)) return url;
      return `${text} (${url})`;
    },
  );
  return stripTags(withLinks).replace(/[ \t]+/g, ' ').trim();
}

function partToText(part: FaqAnswerPart, rootUrl: string): string {
  if (Array.isArray(part)) {
    return part
      .map((li) => `- ${inlineToText(li, rootUrl)}`)
      .filter((line) => line !== '- ')
      .join('\n');
  }
  return inlineToText(part, rootUrl);
}

function answerToText(parts: FaqAnswerPart[] | undefined, rootUrl: string): string {
  if (!Array.isArray(parts)) return '';
  return parts
    .map((part) => partToText(part, rootUrl))
    .filter(Boolean)
    .join('\n');
}

export function flattenFaq(
  input: { intro?: FaqIntro | string; sections?: unknown },
  rootUrl: string,
): FaqEntry[] {
  const entries: FaqEntry[] = [];
  const intro = input.intro;
  if (intro && typeof intro === 'object' && typeof intro.heading === 'string' && intro.heading.trim()) {
    const text = (Array.isArray(intro.body) ? intro.body : [])
      .map((p) => inlineToText(String(p), rootUrl))
      .filter(Boolean)
      .join('\n');
    if (text) entries.push({ id: 'intro', q: intro.heading.trim(), text });
  }

  const sections = Array.isArray(input.sections) ? (input.sections as FaqSection[]) : [];
  for (const section of sections) {
    const questions = Array.isArray(section?.questions) ? section.questions : [];
    for (const question of questions) {
      if (!question || typeof question.q !== 'string' || !question.q.trim()) continue;
      const text = answerToText(question.a, rootUrl);
      if (!text) continue;
      entries.push({
        id: typeof question.id === 'string' && question.id ? question.id : question.q,
        q: question.q.trim(),
        text,
      });
    }
  }
  return entries;
}

export function stillStuckText(stuck: FaqStillStuck | string | undefined, rootUrl: string): string {
  if (!stuck || typeof stuck !== 'object') return '';
  const lines: string[] = [];
  if (typeof stuck.heading === 'string' && stuck.heading.trim()) lines.push(stuck.heading.trim());
  for (const part of Array.isArray(stuck.body) ? stuck.body : []) {
    const text = inlineToText(String(part), rootUrl);
    if (text) lines.push(text);
  }
  return lines.join('\n');
}

// Scores the question text only. A hit needs real content-word overlap, or a
// short query that hits one distinctive word (df === 1), such as "free".
export function lookupFaq(entries: FaqEntry[], query: string): FaqEntry | null {
  const qTokens = words(query);
  const qContent = contentWords(qTokens);
  const qQuestion = qTokens.filter((w) => QUESTION_WORDS.has(w));
  if (!qContent.length) return null;

  const df = new Map<string, number>();
  const prepared = entries.map((entry) => {
    const tokens = words(entry.q);
    const content = contentWords(tokens);
    for (const w of new Set(content)) df.set(w, (df.get(w) ?? 0) + 1);
    return { entry, tokens, content };
  });

  let best: FaqEntry | null = null;
  let bestScore = 0;
  for (const row of prepared) {
    if (!row.content.length) continue;
    const contentSet = new Set(row.content);
    const overlap = qContent.filter((w) => contentSet.has(w));
    if (!overlap.length) continue;

    const coverage = overlap.length / qContent.length;
    const distinctive = overlap.some((w) => (df.get(w) ?? 0) === 1 && w.length >= 4);
    const confident =
      (overlap.length >= 2 && coverage >= 0.5) ||
      (qContent.length <= 2 && overlap.length === 1 && distinctive && coverage >= 0.5);
    if (!confident) continue;

    const questionSet = new Set(row.tokens.filter((w) => QUESTION_WORDS.has(w)));
    const questionOverlap = qQuestion.filter((w) => questionSet.has(w)).length;
    const precision = overlap.length / row.content.length;
    const score = precision + coverage * 0.6 + questionOverlap * 0.75;
    if (score > bestScore) {
      bestScore = score;
      best = row.entry;
    }
  }
  return best;
}
